import NotionPropertyEditor from "./NotionPropertyEditor";
import NotionTaskCreator from "./NotionTaskCreator";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  documentService as service,
  assistantService,
} from "../../services/api";

const areas = [
  "work",
  "family",
  "health",
  "finance",
  "maintenance",
  "travel",
  "learning",
  "leisure",
];
const errorText = (error) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string"
    ? detail
    : Array.isArray(detail)
      ? detail.map((x) => x.msg).join("; ")
      : "This document action could not be completed. Try again.";
};
const timestamp = (value) =>
  value ? new Date(value).toLocaleString() : "Not synced yet";
const providerName = value => ({docs: 'Google Docs', notion: 'Notion', drive_file: 'Google Drive file'}[value] || value);
const fileType = value => ({'application/pdf': 'PDF', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word (.docx)', 'text/plain': 'Text', 'text/markdown': 'Markdown', 'text/csv': 'CSV', 'text/tab-separated-values': 'TSV'}[value] || 'Google document');
function ErrorMessage({ error }) {
  return error ? (
    <p role="alert" className="text-sm text-red-700">
      {errorText(error)}
    </p>
  ) : null;
}
function useRequest() {
  const last = useRef(null);
  return (value) => {
    const key = JSON.stringify(value);
    if (last.current?.key !== key)
      last.current = { key, id: crypto.randomUUID() };
    return { ...value, request_id: last.current.id };
  };
}
function NotionRows({ snapshot, onSelect }) {
  if (snapshot.object_type !== "data_source") return null;
  return <section className="space-y-3" aria-label="Notion database rows">
    <p className="text-sm">{snapshot.row_count} {snapshot.row_count === 1 ? "row" : "rows"} imported{snapshot.limited ? " · incomplete snapshot" : ""}. Open a row to preview and monitor its full page notes separately.</p>
    <div className="max-h-64 overflow-auto space-y-2">
      {(snapshot.rows || []).map(row => <div key={row.id} className="flex flex-wrap items-center justify-between gap-2 rounded border p-2">
        <a href={row.url} target="_blank" rel="noreferrer" className="text-sm underline">{row.title}</a>
        <button className="secondary-button text-xs" onClick={() => onSelect({provider: "notion", external_id: row.id, object_type: "page"})}>Preview row {row.title}</button>
      </div>)}
    </div>
  </section>;
}

export function DocumentSnapshot({ snapshot }) {
  return (
    <section className="space-y-4" aria-label="Document snapshot">
      {snapshot.provider === 'drive_file' && <p className="text-sm text-[#66716a]">{fileType(snapshot.mime_type)} · {Math.ceil((snapshot.size_bytes || 0) / 1024)} KiB · File version {snapshot.provider_revision}. Only extracted text is saved; the original file stays in Drive.</p>}
      {(snapshot.warnings || []).map((w) => (
        <p
          key={w}
          className="rounded-md bg-amber-50 p-3 text-sm text-amber-900"
        >
          {w}
        </p>
      ))}
      <p className="text-xs text-[#66716a]">
        Plain-text snapshot. Formatting, attachments and linked pages may
        require opening the original.
      </p>
      {snapshot.sections?.length ? (
        snapshot.sections.map((section, index) => (
          <section
            key={`${section.key}-${index}`}
            className="border-l-2 border-[#dce3dd] pl-4"
            style={{ marginLeft: Math.min(section.depth || 0, 4) * 12 }}
          >
            {section.title && (
              <h3 className="mb-2 font-bold">{section.title}</h3>
            )}
            <div className="whitespace-pre-wrap break-words text-sm leading-6">
              {section.text || "This section has no readable text."}
            </div>
          </section>
        ))
      ) : (
        <p className="whitespace-pre-wrap break-words text-sm">
          {snapshot.text || "No readable text is available."}
        </p>
      )}
    </section>
  );
}

function SourceBrowser() {
  const location = useLocation(), initialPreview = useRef(false);
  const navigate = useNavigate(),
    client = useQueryClient(),
    request = useRequest();
  const [provider, setProvider] = useState("drive"),
    [query, setQuery] = useState(""),
    [search, setSearch] = useState(null),
    [cursor, setCursor] = useState(null),
    [previous, setPrevious] = useState([]);
  const [directProvider, setDirectProvider] = useState("docs"),
    [identifier, setIdentifier] = useState(""),
    [selection, setSelection] = useState(null),
    [domain, setDomain] = useState("work");
  const results = useQuery({
    queryKey: ["document-search", search, cursor],
    queryFn: () => service.browse({ ...search, cursor }),
    enabled: !!search,
    staleTime: 0,
    retry: false,
  });
  const preview = useMutation({
    mutationFn: service.preview,
    onSuccess: (data) => setSelection(data),
  });
  const monitor = useMutation({
    mutationFn: service.monitor,
    onSuccess: async (result) => {
      await client.invalidateQueries({ queryKey: ["documents"] });
      navigate(`/documents/${result.source_id}`);
    },
  });
  const open = (item) => {
    setSelection(null);
    monitor.reset();
    preview.mutate({ provider: item.provider, external_id: item.external_id, ...(item.object_type ? { object_type: item.object_type } : {}) });
  };
  const requestedPreview = location.state?.preview;
  const previewDocument = preview.mutate;
  useEffect(() => {
    if (requestedPreview && !initialPreview.current) {
      initialPreview.current = true;
      previewDocument(requestedPreview);
    }
  }, [requestedPreview, previewDocument]);
  return (
    <section className="surface-panel p-5 space-y-5">
      <div>
        <h2 className="text-lg font-bold">Find a document to monitor</h2>
        <p className="mt-2 text-sm text-[#66716a]">
          Search titles in connected Google Drive or pages shared with your
          Notion integration, including database properties. Drive supports Google Docs, PDF, Word (.docx), plain text, Markdown and CSV files. Preview a result, then choose whether Timelyzer
          should monitor it. File imports are limited to 10 MiB; scanned pages need OCR before their text can be analysed.
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(provider === "notion_data_source" ? { provider: "notion", query, object_type: "data_source" } : { provider, query });
          setCursor(null);
          setPrevious([]);
        }}
        className="grid gap-3 lg:grid-cols-[280px_1fr_auto]"
      >
        <label className="text-sm font-semibold">
          Search provider
          <select
            className="field-input mt-1"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          >
            <option value="drive">Google Drive · Documents and files</option>
            <option value="notion">Notion pages</option>
            <option value="notion_data_source">Notion databases</option>
          </select>
        </label>
        <label className="text-sm font-semibold">
          Document title
          <input
            className="field-input mt-1"
            maxLength={180}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Project notes, routine, trip…"
          />
        </label>
        <button
          className="primary-button self-end"
          disabled={results.isFetching}
        >
          Search documents
        </button>
      </form>
      <p className="text-xs text-[#66716a]">
        Drive search lists supported documents and files, including accessible shared-drive files.
        Notion search matches page or database titles, not their content. Databases may contain several data sources; select the one you need. Newly shared sources may take time to appear.{" "}
        <Link className="underline" to="/assistant?view=connections">
          Manage connections
        </Link>
      </p>
      {results.isFetching && <p role="status">Searching connected provider…</p>}
      <ErrorMessage error={results.error} />
      {results.data && (
        <div className="space-y-3" aria-label="Document search results">
          {!results.data.items.length && (
            <p>No matching documents on this page.</p>
          )}
          {results.data.limited && (
            <p className="text-sm text-amber-900">
              The provider returned incomplete search results. Narrow the title
              or paste a document link below.
            </p>
          )}
          {results.data.items.map((item) => (
            <article
              key={item.external_id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#dce3dd] p-3"
            >
              <div>
                <h3 className="font-semibold">{item.title}</h3>
                <p className="text-xs text-[#66716a]">
                  {item.provider !== "notion" ? providerName(item.provider) : item.object_type === "data_source" ? "Notion data source" : "Notion page"} ·{" "}
                  {timestamp(item.modified_at)}
                </p>
              </div>
              {item.source_id ? (
                <Link
                  className="secondary-button text-xs"
                  to={`/documents/${item.source_id}`}
                >
                  Open monitored document
                </Link>
              ) : (
                <button
                  className="secondary-button text-xs"
                  disabled={preview.isPending || !!item.unavailable_reason}
                  onClick={() => open(item)}
                >
                  Preview {item.title}
                </button>
              )}
              {item.provider === 'drive_file' && <p className="text-xs text-[#66716a]">{fileType(item.mime_type)}{item.size_bytes != null ? ` · ${Math.ceil(item.size_bytes / 1024)} KiB` : ''}{item.unavailable_reason ? ` · ${item.unavailable_reason}` : ''}</p>}
            </article>
          ))}
          <div className="flex gap-3">
            <button
              className="quiet-button"
              disabled={!previous.length || results.isFetching}
              onClick={() => {
                setCursor(previous[previous.length - 1]);
                setPrevious(previous.slice(0, -1));
              }}
            >
              Previous search page
            </button>
            <button
              className="quiet-button"
              disabled={!results.data.next_cursor || results.isFetching}
              onClick={() => {
                setPrevious([...previous, cursor]);
                setCursor(results.data.next_cursor);
              }}
            >
              Next search page
            </button>
          </div>
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          open({ provider: directProvider.startsWith("notion") ? "notion" : directProvider, external_id: identifier,
            ...(directProvider.startsWith("notion_") ? {object_type: directProvider.slice(7)} : {}) });
        }}
        className="grid gap-3 border-t border-[#dce3dd] pt-5 lg:grid-cols-[240px_1fr_auto]"
      >
        <label className="text-sm font-semibold">
          Link provider
          <select
            className="field-input mt-1"
            value={directProvider}
            onChange={(e) => setDirectProvider(e.target.value)}
          >
            <option value="docs">Google Docs</option>
            <option value="drive_file">Drive PDF, Word or text file</option>
            <option value="notion">Notion page</option>
            <option value="notion_database">Notion database link</option>
            <option value="notion_data_source">Notion data source ID</option>
          </select>
        </label>
        <label className="text-sm font-semibold">
          Document URL or ID
          <input
            required
            maxLength={2000}
            className="field-input mt-1"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          />
        </label>
        <button
          className="secondary-button self-end"
          disabled={preview.isPending}
        >
          Preview document
        </button>
      </form>
      {preview.isPending && <p role="status">Reading the selected document…</p>}
      <ErrorMessage error={preview.error} />
      {selection?.object_type === "database" && <section className="space-y-3 rounded border p-4" aria-label="Choose Notion data source">
        <h3 className="font-bold">{selection.title}</h3>
        <p className="text-sm">Choose the data source to preview. Monitoring starts only after you review its rows.</p>
        {!selection.data_sources.length && <p>No accessible data sources were returned. Check the integration's access in Notion.</p>}
        {selection.data_sources.map(item => <button key={item.external_id} className="secondary-button mr-2" onClick={() => open(item)} disabled={preview.isPending}>Preview data source {item.title}</button>)}
      </section>}
      {selection && selection.object_type !== "database" && (
        <section
          className="space-y-4 rounded-md border border-[#dce3dd] bg-[#f7faf7] p-4"
          aria-label="Preview selected document"
        >
          <header className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-bold">{selection.title}</h2>
            <a
              className="text-sm underline"
              href={selection.url}
              target="_blank"
              rel="noreferrer"
            >
              Open original
            </a>
          </header>
          <NotionRows snapshot={selection} onSelect={open} />
          <div className="max-h-96 overflow-auto">
            <DocumentSnapshot snapshot={selection} />
          </div>
          <label className="block text-sm font-semibold">
            Life area
            <select
              className="field-input mt-1 max-w-xs"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            >
              {areas.map((area) => (
                <option key={area}>{area}</option>
              ))}
            </select>
          </label>
          <p className="text-sm text-[#66716a]">
            Monitoring saves this selected document's readable content in
            Timelyzer. Background sync and automatic analysis follow your
            Assistant settings. {selection.provider === 'drive_file' ? 'The original file is read-only here. File bytes are discarded after extracting text; incomplete snapshots do not trigger automatic analysis.' : selection.object_type === "data_source" ? "Monitor this database to review field updates; select individual rows for their notes." : "External edits are prepared as drafts for review."}
          </p>
          <ErrorMessage error={monitor.error} />
          <button
            className="primary-button"
            disabled={monitor.isPending}
            onClick={() =>
              monitor.mutate(
                request({
                  provider: selection.provider,
                  external_id: selection.external_id,
                  ...(selection.object_type ? {object_type: selection.object_type} : {}),
                  expected_revision: selection.revision,
                  domain,
                }),
              )
            }
          >
            {monitor.isPending
              ? "Enabling monitoring…"
              : "Monitor this document"}
          </button>
        </section>
      )}
    </section>
  );
}

function DocumentActions({ data, refresh }) {
  const [revision, setRevision] = useState(data.revision),
    [title, setTitle] = useState(`${data.title} · excerpt`),
    [excerpt, setExcerpt] = useState(""),
    [body, setBody] = useState(""),
    [tab, setTab] = useState("");
  const captureRequest = useRequest(),
    draftRequest = useRequest();
  const capture = useMutation({
    mutationFn: (payload) => service.capture(data.id, payload),
    onSuccess: refresh,
  });
  const draft = useMutation({
    mutationFn: (payload) => service.draft(data.id, payload),
    onSuccess: refresh,
  });
  const stale = revision !== data.revision;
  return (
    <div className="space-y-5">
      {stale && (
        <section className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3">
          <p className="text-sm">
            The snapshot changed. Your text is preserved. Review the current
            source before preparing a capture or append.
          </p>
          <button
            className="secondary-button text-xs"
            onClick={() => {
              setRevision(data.revision);
              capture.reset();
              draft.reset();
            }}
          >
            Use reviewed current snapshot
          </button>
        </section>
      )}
      <form
        className="surface-panel p-5 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          capture.mutate(
            captureRequest({ expected_revision: revision, title, excerpt }),
          );
        }}
      >
        <h2 className="font-bold">Keep an excerpt in Notes</h2>
        <p className="text-xs text-[#66716a]">
          This saves a separate editable note. Unchanged excerpts reuse source
          analysis. Later source changes do not overwrite it.
        </p>
        <label className="block text-sm font-semibold">
          Note title
          <input
            className="field-input mt-1"
            required
            maxLength={240}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="block text-sm font-semibold">
          Exact source excerpt
          <textarea
            className="field-input mt-1"
            rows={5}
            required
            maxLength={30000}
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="quiet-button text-xs"
          disabled={!data.snapshot.text || data.snapshot.text.length > 30000}
          onClick={() => setExcerpt(data.snapshot.text)}
        >
          Use the full snapshot
        </button>
        <p className="text-xs text-[#66716a]">
          Paste exact source text, up to 30,000 characters. For larger
          documents, choose a shorter excerpt.
        </p>
        <ErrorMessage error={capture.error} />
        {capture.data && (
          <Link
            className="block text-sm underline"
            to={`/notes?note=${capture.data.note_id}`}
          >
            Open captured note
          </Link>
        )}
        <button
          className="primary-button"
          disabled={capture.isPending || stale || !!data.error}
        >
          Save excerpt to Notes
        </button>
      </form>
      {data.provider === 'drive_file' ? <section className="surface-panel p-5 space-y-3 text-sm"><h2 className="font-bold">Original file stays in Drive</h2><p>Use the extracted text for notes, meeting preparation and reviewed follow-ups. To write an update, choose an editable Google Doc or Notion page.</p><Link className="underline" to="/documents">Choose an editable document</Link></section> : data.object_type === "data_source" ? <p className="surface-panel p-5 text-sm">Use Update Notion task fields above to review property changes. Preview and monitor a row page to append to its notes.</p> : <form
        className="surface-panel p-5 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          draft.mutate(
            draftRequest({
              expected_revision: revision,
              body,
              ...(data.provider === "docs" ? { tab_id: tab } : {}),
            }),
          );
        }}
      >
        <h2 className="font-bold">Prepare an update to this document</h2>
        {data.provider === "docs" && (
          <label className="block text-sm font-semibold">
            Destination tab
            <select
              required
              className="field-input mt-1"
              value={tab}
              onChange={(e) => setTab(e.target.value)}
            >
              <option value="">Choose a tab</option>
              {(data.snapshot.sections || []).map((section) => (
                <option key={section.key} value={section.key}>
                  {section.title || "Document body"}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="block text-sm font-semibold">
          Text to append
          <textarea
            className="field-input mt-1"
            required
            rows={6}
            maxLength={20000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </label>
        <p className="text-xs text-[#66716a]">
          Prepare a local draft, then review and apply it in Outbox. The source
          will be checked again before writing.
          {data.provider === "notion"
            ? " A visible Timelyzer update record link is included after your notes for verification. Notion cannot lock out edits made between that check and the append."
            : " Google updates require the reviewed document revision."}
        </p>
        <ErrorMessage error={draft.error} />
        {draft.data && (
          <Link className="block text-sm underline" to="/assistant?view=outbox">
            Review prepared append in Outbox
          </Link>
        )}
        <button
          className="primary-button"
          disabled={
            draft.isPending || stale || !!data.error || data.snapshot.limited
          }
        >
          Prepare append draft
        </button>
      </form>}
    </div>
  );
}

function DocumentWorkspace({ sourceId }) {
  const client = useQueryClient(), navigate = useNavigate();
  const query = useQuery({
    queryKey: ["documents", sourceId],
    queryFn: () => service.get(sourceId),
    retry: false,
    staleTime: 0,
  });
  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ["documents"] }),
      client.invalidateQueries({ queryKey: ["assistant"] }),
      client.invalidateQueries({ queryKey: ["notes"] }),
      client.invalidateQueries({ queryKey: ["agent-work"] }),
    ]);
  const sync = useMutation({
    mutationFn: () => assistantService.sync(sourceId),
    onSettled: refresh,
  });
  const analyse = useMutation({
    mutationFn: (revision) =>
      service.analyse(sourceId, { expected_revision: revision }),
    onSuccess: refresh,
  });
  if (query.isLoading) return <p role="status">Loading document workspace…</p>;
  if (query.error && !query.data)
    return (
      <section className="surface-panel p-5">
        <ErrorMessage error={query.error} />
        <button
          className="secondary-button mt-3"
          onClick={() => query.refetch()}
        >
          Reload document
        </button>
      </section>
    );
  const data = query.data;
  return (
    <>
      <header className="surface-panel p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold">{data.title}</h2>
          <a
            className="secondary-button text-sm"
            href={data.url}
            target="_blank"
            rel="noreferrer"
          >
            Open original document
          </a>
        </div>
        <p className="text-sm text-[#66716a]">
          {providerName(data.provider)} · {data.domain}{" "}
          · Last synced {timestamp(data.synced_at)}
        </p>
        {data.error && (
          <p role="alert" className="text-sm text-red-700">
            {data.error} The saved snapshot may be out of date.
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          <button
            className="secondary-button"
            disabled={sync.isPending}
            onClick={() => sync.mutate()}
          >
            {sync.isPending ? "Syncing…" : "Sync document"}
          </button>
          <button
            className="primary-button"
            disabled={
              !data.analysis_ready || analyse.isPending || sync.isPending
            }
            onClick={() => analyse.mutate(data.revision)}
          >
            Analyse for follow-ups
          </button>
          <Link className="quiet-button" to="/assistant?view=connections">
            Manage monitoring
          </Link>
        </div>
        <ErrorMessage error={query.error || sync.error || analyse.error} />
        {query.error && <button className="secondary-button" onClick={() => query.refetch()}>Retry workspace refresh</button>}
        {analyse.isSuccess && (
          <p role="status" className="text-sm">
            Analysis requested within your AI allowance. Open Agent review to
            see its result.
          </p>
        )}
        <div className="rounded-md bg-[#f1f5f1] p-3 text-sm">
          <p>
            {data.analysis.pending_count} proposed changes awaiting review ·
            Latest analysis: {data.analysis.latest?.status || "Not run"}
          </p>
          {data.analysis.latest?.current === false && (
            <p className="mt-2 text-amber-900">
              The source changed since the latest analysis. Analyse it again to
              review current follow-ups.
            </p>
          )}
          {data.analysis.latest?.error && (
            <p role="alert" className="text-red-700">
              {data.analysis.latest.error}
            </p>
          )}
          <Link className="underline" to="/assistant?view=agent">
            Open Agent review
          </Link>
          <button
            className="quiet-button ml-3 text-xs"
            onClick={() => query.refetch()}
          >
            Refresh analysis status
          </button>
        </div>
      </header>
      {data.provider === "notion" && <NotionPropertyEditor key={data.id} data={data} refresh={refresh} />}
      {data.provider === "notion" && data.snapshot?.object_type === "data_source" && <NotionTaskCreator key={`create-${data.id}`} data={data} refresh={refresh} />}
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(320px,1fr)]">
        <section className="surface-panel p-5 min-w-0">
          <h2 className="mb-4 font-bold">Saved source content</h2>
          <NotionRows snapshot={data.snapshot} onSelect={(preview) => navigate("/documents", {state: {preview}})} />
          <DocumentSnapshot snapshot={data.snapshot} />
        </section>
        <DocumentActions data={data} refresh={refresh} />
      </div>
      {!!data.captures.length && (
        <section className="surface-panel p-5">
          <h2 className="font-bold mb-3">Captured notes</h2>
          {data.captures.map((note) => (
            <Link
              key={note.note_id}
              className="block py-2 text-sm underline"
              to={`/notes?note=${note.note_id}`}
            >
              {note.title}
            </Link>
          ))}
        </section>
      )}
      {!!data.drafts.length && (
        <section className="surface-panel p-5">
          <h2 className="font-bold mb-3">Document append history</h2>
          {data.drafts.map((draft) => (
            <article
              key={draft.id}
              className="border-t border-[#dce3dd] py-3 space-y-2"
            >
              <p className="text-sm font-semibold">
                {draft.tab_title || draft.title} ·{" "}
                {draft.status === "sent"
                  ? "Provider accepted"
                  : draft.status.replaceAll("_", " ")}
              </p>
              <p className="text-sm whitespace-pre-wrap">{draft.body}</p>
              {draft.result?.message && (
                <p className="text-xs text-[#66716a]">{draft.result.message}</p>
              )}
            </article>
          ))}
          <Link className="text-sm underline" to="/assistant?view=outbox">
            Open Outbox
          </Link>
        </section>
      )}
    </>
  );
}

export default function DocumentsPage() {
  const { sourceId } = useParams(),
    [offset, setOffset] = useState(0);
  const list = useQuery({
    queryKey: ["documents", "list", offset],
    queryFn: () => service.list(offset),
    enabled: !sourceId,
    staleTime: 0,
  });
  return (
    <div className="app-page">
      <div className="app-page-inner space-y-5">
        <header className="page-header">
          <div>
            <p className="eyebrow">Connected source workspace</p>
            <h1 className="page-title">Documents</h1>
            <p className="page-description">
              Read existing notes, follow changes, and prepare updates with
              their source attached.
            </p>
          </div>
          {sourceId && (
            <Link className="secondary-button" to="/documents">
              All documents
            </Link>
          )}
        </header>
        {sourceId ? (
          <DocumentWorkspace key={sourceId} sourceId={Number(sourceId)} />
        ) : (
          <>
            <SourceBrowser />
            <section className="surface-panel p-5 space-y-4">
              <h2 className="text-lg font-bold">Monitored documents</h2>
              {list.isLoading && (
                <p role="status">Loading monitored documents…</p>
              )}
              <ErrorMessage error={list.error} />
              {list.data && (
                <>
                  <p className="text-sm text-[#66716a]">
                    Background monitoring:{" "}
                    {list.data.monitoring_enabled ? "enabled" : "off"} ·
                    Automatic analysis:{" "}
                    {list.data.automatic_analysis ? "enabled" : "off"}.{" "}
                    <Link className="underline" to="/assistant?view=settings">
                      Assistant settings
                    </Link>
                  </p>
                  {!list.data.items.length && (
                    <p className="text-sm">Choose a document above to begin.</p>
                  )}
                  {list.data.items.map((item) => (
                    <article
                      key={item.id}
                      className="flex flex-wrap items-center justify-between gap-3 border-t border-[#dce3dd] py-3"
                    >
                      <div>
                        <h3 className="font-bold">{item.title}</h3>
                        <p className="text-xs text-[#66716a]">
                          {providerName(item.provider)} · {item.domain} ·{" "}
                          {timestamp(item.synced_at)}
                        </p>
                        {item.error && (
                          <p className="text-sm text-red-700">{item.error}</p>
                        )}
                      </div>
                      <Link
                        className="secondary-button text-sm"
                        to={`/documents/${item.id}`}
                      >
                        Open {item.title}
                      </Link>
                    </article>
                  ))}
                  <div className="flex gap-3">
                    <button
                      className="quiet-button"
                      disabled={!offset}
                      onClick={() => setOffset(Math.max(0, offset - 30))}
                    >
                      Previous documents
                    </button>
                    <button
                      className="quiet-button"
                      disabled={list.data.next_offset == null}
                      onClick={() => setOffset(list.data.next_offset)}
                    >
                      Next documents
                    </button>
                  </div>
                </>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
